import React, { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import {Search, Filter, ChevronDown, Edit3, Trash2, X, Check, Coffee, Building2, Briefcase, Hotel, Package, MoreHorizontal, Phone, Mail, Calendar, MessageSquare, UserCheck, RefreshCw, TrendingUp, CheckCircle, AlertCircle} from 'lucide-react'

interface B2BLead {
  _id: string
  contactName: string
  companyName?: string
  phone: string
  email?: string
  clientType: string
  monthlyUsage: string
  message?: string
  status: string
  source?: string
  assignedTo?: string
  notes?: string
  followUpDate?: string
  createdAt: string
  updatedAt: string
}

const clientTypeLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  cafe:   { label: '咖啡廳/選物店', icon: <Coffee size={14}/>, color: 'bg-amber-900 text-amber-300' },
  brunch: { label: '早午餐/餐酒館', icon: <Building2 size={14}/>, color: 'bg-rose-900 text-rose-300' },
  office: { label: '企業辦公室', icon: <Briefcase size={14}/>, color: 'bg-blue-900 text-blue-300' },
  hotel:  { label: '飯店旅宿', icon: <Hotel size={14}/>, color: 'bg-purple-900 text-purple-300' },
  oem:    { label: '濾掛 OEM', icon: <Package size={14}/>, color: 'bg-green-900 text-green-300' },
  other:  { label: '其他', icon: <MoreHorizontal size={14}/>, color: 'bg-gray-800 text-gray-300' },
}

const usageLabels: Record<string, string> = {
  under5kg: '< 5kg',
  '5to20kg': '5–20kg',
  over20kg: '20kg+',
}

const statusConfig: Record<string, { label: string; color: string }> = {
  new:          { label: '新詢問', color: 'bg-sky-600' },
  contacted:    { label: '已聯繫', color: 'bg-blue-600' },
  sample_sent:  { label: '樣本已寄', color: 'bg-amber-600' },
  negotiating:  { label: '洽談中', color: 'bg-purple-600' },
  closed_won:   { label: '成交', color: 'bg-green-600' },
  closed_lost:  { label: '未成交', color: 'bg-gray-600' },
}

const PIPELINE_STAGES = Object.entries(statusConfig)

const B2BLeadsManager: React.FC = () => {
  const [leads, setLeads] = useState<B2BLead[]>([])
  const [filtered, setFiltered] = useState<B2BLead[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [selected, setSelected] = useState<B2BLead | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [editNotes, setEditNotes] = useState('')
  const [editFollowUp, setEditFollowUp] = useState('')
  const [saving, setSaving] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'pipeline'>('list')
  const [approving, setApproving] = useState<string | null>(null)
  const [approvalMsg, setApprovalMsg] = useState<{ id: string; type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => { fetchLeads() }, [])

  useEffect(() => {
    let result = leads
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(l =>
        l.contactName?.toLowerCase().includes(q) ||
        l.companyName?.toLowerCase().includes(q) ||
        l.phone?.includes(q) ||
        l.email?.toLowerCase().includes(q)
      )
    }
    if (filterStatus !== 'all') result = result.filter(l => l.status === filterStatus)
    if (filterType !== 'all') result = result.filter(l => l.clientType === filterType)
    setFiltered(result)
  }, [leads, search, filterStatus, filterType])

  const fetchLeads = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.b2b_leads.list({ sort: { createdAt: -1 } })
      setLeads(list || [])
    } catch (e) {
      console.error('Failed to fetch B2B leads:', e)
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (id: string, status: string) => {
    try {
      await lumi.entities.b2b_leads.update(id, { status, updatedAt: new Date().toISOString() })
      setLeads(prev => prev.map(l => l._id === id ? { ...l, status } : l))
      if (selected?._id === id) setSelected(prev => prev ? { ...prev, status } : null)
    } catch (e) {
      console.error('Status update failed:', e)
    }
  }

  const saveDetail = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await lumi.entities.b2b_leads.update(selected._id, {
        notes: editNotes,
        followUpDate: editFollowUp,
        updatedAt: new Date().toISOString(),
      })
      setLeads(prev => prev.map(l => l._id === selected._id
        ? { ...l, notes: editNotes, followUpDate: editFollowUp }
        : l
      ))
      setSelected(prev => prev ? { ...prev, notes: editNotes, followUpDate: editFollowUp } : null)
    } catch (e) {
      console.error('Save failed:', e)
    } finally {
      setSaving(false)
    }
  }

  const deleteLead = async (id: string) => {
    if (!confirm('確定要刪除此詢問紀錄？')) return
    try {
      await lumi.entities.b2b_leads.delete(id)
      setLeads(prev => prev.filter(l => l._id !== id))
      setShowDetail(false)
      setSelected(null)
    } catch (e) {
      console.error('Delete failed:', e)
    }
  }

  // 審核通過：建立/更新 customers 為 B2B 身份 + 寄通知信
  const approveLead = async (lead: B2BLead) => {
    if (!lead.email) {
      setApprovalMsg({ id: lead._id, type: 'error', text: '此詢問無 Email，無法審核通過' })
      return
    }
    if (!confirm(`確定審核通過「${lead.contactName}」並發送開通通知信？`)) return
    setApproving(lead._id)
    setApprovalMsg(null)
    try {
      // 1. 更新 b2b_leads 狀態為 closed_won
      await lumi.entities.b2b_leads.update(lead._id, {
        status: 'closed_won',
        updatedAt: new Date().toISOString(),
      })
      setLeads(prev => prev.map(l => l._id === lead._id ? { ...l, status: 'closed_won' } : l))

      // 2. 查詢是否已有對應 customer，有則升級為 b2b，無則建立
      const { list: existingCustomers } = await lumi.entities.customers.list({
        filter: { email: lead.email },
        limit: 1,
      })

      if (existingCustomers && existingCustomers.length > 0) {
        await lumi.entities.customers.update(existingCustomers[0]._id, {
          customerType: 'b2b',
          companyName: lead.companyName || '',
          contactPerson: lead.contactName,
          updatedAt: new Date().toISOString(),
        })
      } else {
        await lumi.entities.customers.create({
          userId: '',
          userName: lead.contactName,
          email: lead.email,
          phone: lead.phone,
          customerType: 'b2b',
          companyName: lead.companyName || '',
          contactPerson: lead.contactName,
          memberLevel: 'LV1',
          status: 'active',
          totalSpent: 0,
          orderCount: 0,
          points: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          creator: 'admin',
        })
      }

      // 3. 呼叫 Deno Function 寄送審核通過通知信
      try {
        await fetch('https://api.lumi.new/v1/functions/p373839333138763776/sendB2BApproval', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contactName: lead.contactName,
            companyName: lead.companyName || '',
            email: lead.email,
            clientType: lead.clientType,
          }),
        })
      } catch (emailErr) {
        console.error('Email send failed (non-critical):', emailErr)
      }

      setApprovalMsg({ id: lead._id, type: 'success', text: `已審核通過，通知信已寄至 ${lead.email}` })
    } catch (e) {
      console.error('Approval failed:', e)
      setApprovalMsg({ id: lead._id, type: 'error', text: '審核失敗，請稍後再試' })
    } finally {
      setApproving(null)
    }
  }

  const openDetail = (lead: B2BLead) => {
    setSelected(lead)
    setEditNotes(lead.notes || '')
    setEditFollowUp(lead.followUpDate || '')
    setShowDetail(true)
  }

  // 統計
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    active: leads.filter(l => ['contacted', 'sample_sent', 'negotiating'].includes(l.status)).length,
    won: leads.filter(l => l.status === 'closed_won').length,
  }

  return (
    <div className="p-8 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-1">B2B 詢問管理</h2>
          <p className="text-gray-400 text-sm">試豆申請 · 跟進進度 · 轉化追蹤</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'pipeline' : 'list')}
            className="px-4 py-2 border border-gray-700 text-gray-300 hover:border-gray-500 text-sm transition-colors"
          >
            {viewMode === 'list' ? '看板視圖' : '列表視圖'}
          </button>
          <button
            onClick={fetchLeads}
            className="flex items-center gap-2 px-4 py-2 border border-gray-700 text-gray-300 hover:border-gray-500 transition-colors"
          >
            <RefreshCw size={16} />
            <span className="text-sm">重整</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: '總詢問', value: stats.total, color: 'text-gray-100' },
          { label: '新詢問', value: stats.new, color: 'text-sky-400' },
          { label: '洽談中', value: stats.active, color: 'text-amber-400' },
          { label: '已成交', value: stats.won, color: 'text-green-400' },
        ].map(s => (
          <div key={s.label} className="bg-gray-950 border border-gray-800 p-4">
            <div className={`text-2xl font-light mb-1 ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜尋姓名、公司、電話..."
            className="w-full pl-9 pr-4 py-2.5 bg-gray-950 border border-gray-800 text-gray-100 text-sm focus:outline-none focus:border-gray-600"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2.5 bg-gray-950 border border-gray-800 text-gray-300 text-sm focus:outline-none focus:border-gray-600"
        >
          <option value="all">所有狀態</option>
          {PIPELINE_STAGES.map(([v, c]) => (
            <option key={v} value={v}>{c.label}</option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2.5 bg-gray-950 border border-gray-800 text-gray-300 text-sm focus:outline-none focus:border-gray-600"
        >
          <option value="all">所有業態</option>
          {Object.entries(clientTypeLabels).map(([v, c]) => (
            <option key={v} value={v}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Pipeline View */}
      {viewMode === 'pipeline' ? (
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-4 min-w-max pb-4">
            {PIPELINE_STAGES.map(([stage, cfg]) => {
              const stageLeads = leads.filter(l => l.status === stage)
              return (
                <div key={stage} className="w-64 flex-shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${cfg.color}`}></span>
                      <span className="text-xs font-light tracking-wider text-gray-300 uppercase">{cfg.label}</span>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5">{stageLeads.length}</span>
                  </div>
                  <div className="space-y-2">
                    {stageLeads.map(lead => (
                      <div
                        key={lead._id}
                        onClick={() => openDetail(lead)}
                        className="p-3 bg-gray-950 border border-gray-800 hover:border-gray-600 cursor-pointer transition-colors"
                      >
                        <div className="font-light text-gray-100 text-sm mb-1">{lead.contactName}</div>
                        {lead.companyName && <div className="text-xs text-gray-500 mb-2">{lead.companyName}</div>}
                        <div className="flex items-center justify-between">
                          <span className={`text-xs px-2 py-0.5 ${clientTypeLabels[lead.clientType]?.color || 'bg-gray-800 text-gray-400'}`}>
                            {clientTypeLabels[lead.clientType]?.label || lead.clientType}
                          </span>
                          <span className="text-xs text-gray-500">{usageLabels[lead.monthlyUsage] || lead.monthlyUsage}</span>
                        </div>
                      </div>
                    ))}
                    {stageLeads.length === 0 && (
                      <div className="py-8 text-center text-gray-700 text-xs">— 無資料 —</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-16 text-gray-500">載入中...</div>
          ) : (
            <div className="bg-gray-950 border border-gray-800 overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-800">
                  <tr className="text-left">
                    <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">聯絡人</th>
                    <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">業態</th>
                    <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">月用量</th>
                    <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">狀態</th>
                    <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">申請時間</th>
                    <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">跟進</th>
                    <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(lead => (
                    <tr key={lead._id} className="border-b border-gray-800 hover:bg-gray-900 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-light text-gray-100">{lead.contactName}</div>
                        {lead.companyName && <div className="text-xs text-gray-500">{lead.companyName}</div>}
                        <div className="text-xs text-gray-500 mt-0.5">{lead.phone}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs ${clientTypeLabels[lead.clientType]?.color || 'bg-gray-800 text-gray-400'}`}>
                          {clientTypeLabels[lead.clientType]?.icon}
                          {clientTypeLabels[lead.clientType]?.label || lead.clientType}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-gray-300 font-light">{usageLabels[lead.monthlyUsage] || lead.monthlyUsage}</span>
                      </td>
                      <td className="px-5 py-4">
                        <select
                          value={lead.status}
                          onChange={e => updateStatus(lead._id, e.target.value)}
                          onClick={e => e.stopPropagation()}
                          className={`px-2 py-1 text-xs text-white border-0 focus:outline-none cursor-pointer ${statusConfig[lead.status]?.color || 'bg-gray-700'}`}
                        >
                          {PIPELINE_STAGES.map(([v, c]) => (
                            <option key={v} value={v} className="bg-gray-900">{c.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-4 text-gray-400 text-sm font-light">
                        {new Date(lead.createdAt).toLocaleDateString('zh-TW')}
                      </td>
                      <td className="px-5 py-4">
                        {lead.followUpDate ? (
                          <span className="text-xs text-amber-400">{new Date(lead.followUpDate).toLocaleDateString('zh-TW')}</span>
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1.5">
                          <button
                            onClick={() => openDetail(lead)}
                            className="px-3 py-1.5 border border-gray-700 text-gray-300 text-xs hover:border-gray-500 transition-colors"
                          >
                            詳情
                          </button>
                          {lead.status !== 'closed_won' && lead.email && (
                            <button
                              onClick={() => approveLead(lead)}
                              disabled={approving === lead._id}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-800 hover:bg-green-700 text-green-200 text-xs transition-colors disabled:opacity-50"
                            >
                              {approving === lead._id
                                ? <RefreshCw size={10} className="animate-spin" />
                                : <CheckCircle size={10} />}
                              審核通過
                            </button>
                          )}
                          {approvalMsg?.id === lead._id && (
                            <p className={`text-xs mt-0.5 ${approvalMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                              {approvalMsg.text}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-16 text-gray-500 text-sm">
                  {leads.length === 0 ? '尚無 B2B 詢問資料' : '無符合條件的資料'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-950 border border-gray-800 w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-800 flex items-start justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-light tracking-wider text-gray-100">{selected.contactName}</h3>
                {selected.companyName && <p className="text-sm text-gray-400 mt-1">{selected.companyName}</p>}
                <div className="flex items-center gap-3 mt-2">
                  <span className={`text-xs px-2 py-1 ${statusConfig[selected.status]?.color || 'bg-gray-700'} text-white`}>
                    {statusConfig[selected.status]?.label}
                  </span>
                  <span className={`text-xs px-2 py-1 ${clientTypeLabels[selected.clientType]?.color || 'bg-gray-800'}`}>
                    {clientTypeLabels[selected.clientType]?.label || selected.clientType}
                  </span>
                </div>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-gray-500 hover:text-gray-200 ml-4">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* 聯絡資訊 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-gray-500" />
                  <span className="text-gray-300 font-light">{selected.phone}</span>
                </div>
                {selected.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail size={14} className="text-gray-500" />
                    <span className="text-gray-300 font-light">{selected.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp size={14} className="text-gray-500" />
                  <span className="text-gray-300 font-light">{usageLabels[selected.monthlyUsage] || selected.monthlyUsage} / 月</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar size={14} className="text-gray-500" />
                  <span className="text-gray-400 font-light">{new Date(selected.createdAt).toLocaleString('zh-TW')}</span>
                </div>
              </div>

              {/* 客戶補充說明 */}
              {selected.message && (
                <div className="p-4 bg-gray-900 border-l-2 border-gray-600">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">客戶說明</div>
                  <p className="text-sm font-light text-gray-300 leading-relaxed">{selected.message}</p>
                </div>
              )}

              {/* 更新狀態 */}
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">更新跟進狀態</div>
                <div className="flex flex-wrap gap-2">
                  {PIPELINE_STAGES.map(([stage, cfg]) => (
                    <button
                      key={stage}
                      onClick={() => updateStatus(selected._id, stage)}
                      className={`px-3 py-1.5 text-xs transition-all ${
                        selected.status === stage
                          ? `${cfg.color} text-white ring-1 ring-white/30`
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {selected.status === stage && <Check size={10} className="inline mr-1" />}
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 下次跟進日期 */}
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">下次跟進日期</label>
                <input
                  type="date"
                  value={editFollowUp}
                  onChange={e => setEditFollowUp(e.target.value)}
                  className="px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm focus:outline-none focus:border-gray-500"
                />
              </div>

              {/* 內部備註 */}
              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">內部備註</label>
                <textarea
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  rows={4}
                  placeholder="記錄通話內容、拜訪情況、報價資訊等..."
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm font-light focus:outline-none focus:border-gray-500 resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-800 flex justify-between flex-shrink-0">
              <button
                onClick={() => deleteLead(selected._id)}
                className="flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 text-sm transition-colors"
              >
                <Trash2 size={16} />
                刪除紀錄
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDetail(false)}
                  className="px-5 py-2 border border-gray-700 text-gray-300 text-sm hover:border-gray-500 transition-colors"
                >
                  關閉
                </button>
                <button
                  onClick={saveDetail}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                  儲存備註
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default B2BLeadsManager
